import React, { useState } from 'react';
import { Message } from '../../types/messages';
import { MessageItem } from './MessageItem';
import { playNotificationSound } from '../../lib/soundNotification';
import { sendToTerminal } from '../../lib/terminalLogger';
import { X } from 'lucide-react';

interface UrgentMessageModalProps {
  message: Message;
  isOpen: boolean;
  onMarkAsRead: (messageId: number) => Promise<any>;
  onClose: () => void;
}

/**
 * Modal bloqueante para mensagens urgentes
 * Não pode ser fechado sem marcar como lida
 */
export function UrgentMessageModal({
  message,
  isOpen,
  onMarkAsRead,
  onClose
}: UrgentMessageModalProps) {
  const [isMarkingAsRead, setIsMarkingAsRead] = useState(false);

  // Tocar som ao abrir
  React.useEffect(() => {
    if (isOpen) {
      playNotificationSound('urgente');
      sendToTerminal('🔔 [MESSAGES] Modal urgente aberto', { messageId: message.id });
    }
  }, [isOpen, message.id]);

  const handleMarkAsReadAndClose = async () => {
    try {
      setIsMarkingAsRead(true);
      await onMarkAsRead(message.id);
      onClose();
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    } finally {
      setIsMarkingAsRead(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm flex items-center justify-center z-[9999] p-3">
      <div className="bg-navy-800 rounded-lg shadow-xl w-full max-w-md mx-auto animate-fadeIn">
        {/* Header sem botão de fechar */}
        <div className="flex justify-between items-center p-3 border-b border-navy-700">
          <h2 className="text-base font-semibold text-white">Mensagem Urgente</h2>
          {/* Botão X desabilitado visualmente */}
          <button disabled className="p-1.5 text-gray-600 cursor-not-allowed">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 space-y-3 max-h-[75vh] overflow-y-auto">
          <div className="p-2 bg-red-500/10 border border-red-500/30 rounded">
            <p className="text-xs text-red-400 font-medium">
              ⚠️ Esta é uma mensagem urgente. Por favor, leia com atenção.
            </p>
          </div>

          <MessageItem
            message={message}
            onMarkAsRead={onMarkAsRead}
            showReply={message.requer_resposta}
          />

          <div className="flex justify-end pt-3 border-t border-navy-700">
            <button
              onClick={handleMarkAsReadAndClose}
              disabled={isMarkingAsRead}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isMarkingAsRead ? 'Marcando...' : 'Entendi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
